import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { EmptyState, SkeletonTabela } from "../../components/EmptyState";
import { Wallet, ExternalLink, CheckCircle2 } from "lucide-react";

const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const fmtData = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("pt-BR"); } catch { return s; }
};

export default function ClienteFinanceiro() {
  const [dados, setDados] = useState({ em_aberto: [], historico: [], total_em_aberto: 0, qtd_em_aberto: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portal/financeiro")
      .then((r) => setDados(r.data))
      .catch(() => setDados({ em_aberto: [], historico: [], total_em_aberto: 0, qtd_em_aberto: 0 }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-4 max-w-4xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-zinc-900">Financeiro</h1>
        <Card><SkeletonTabela linhas={5} colunas={3} /></Card>
      </div>
    );
  }

  const semNada = dados.em_aberto.length === 0 && dados.historico.length === 0;

  return (
    <div className="space-y-5 max-w-4xl mx-auto" data-testid="page-cliente-financeiro">
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-zinc-900">Financeiro</h1>
        <p className="text-sm text-zinc-500 mt-1">Suas cobranças e histórico de pagamentos</p>
      </div>

      {dados.total_em_aberto > 0 && (
        <Card className="p-4 bg-amber-50 border-amber-200" data-testid="resumo-aberto">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-wide text-amber-700 font-semibold">Total em aberto</p>
              <p className="text-2xl font-bold text-amber-900 mt-1">{fmt(dados.total_em_aberto)}</p>
              <p className="text-xs text-amber-700 mt-1">{dados.qtd_em_aberto} cobrança{dados.qtd_em_aberto !== 1 ? "s" : ""} pendente{dados.qtd_em_aberto !== 1 ? "s" : ""}</p>
            </div>
            <Wallet className="w-10 h-10 text-amber-400" />
          </div>
        </Card>
      )}

      {semNada ? (
        <Card data-testid="cliente-financeiro-empty">
          <EmptyState
            icone={Wallet}
            titulo="Você ainda não possui boletos ou faturas disponíveis."
            descricao="Quando houver lançamentos no seu contrato, eles aparecerão aqui."
          />
        </Card>
      ) : (
        <>
          {dados.em_aberto.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold mb-2 text-zinc-700">Em aberto</h2>
              <Card>
                <div className="divide-y divide-zinc-100">
                  {dados.em_aberto.map((c) => (
                    <div key={c.id || c._id} className="p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`cob-aberto-${c.id || c._id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-zinc-900">{c.descricao || "Cobrança"}</p>
                        <p className="text-xs text-zinc-500 mt-0.5">
                          Vence em {fmtData(c.data_vencimento || c.vencimento)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold">{fmt(c.valor)}</span>
                        {c.link_pagamento ? (
                          <a
                            href={c.link_pagamento}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs px-3 py-1.5 rounded-md bg-orange-600 hover:bg-orange-700 text-white font-medium inline-flex items-center gap-1"
                            data-testid={`link-pag-${c.id || c._id}`}
                          >
                            Pagar <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">Aguardando</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </section>
          )}

          {dados.historico.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold mb-2 text-zinc-700">Histórico</h2>
              <Card>
                <div className="divide-y divide-zinc-100">
                  {dados.historico.map((c) => (
                    <div key={c.id || c._id} className="p-4 flex items-center justify-between gap-4 flex-wrap text-sm" data-testid={`cob-historico-${c.id || c._id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-zinc-700">{c.descricao || "Pagamento"}</p>
                        <p className="text-xs text-zinc-400 mt-0.5">
                          {fmtData(c.data_pagamento || c.atualizado_em || c.criado_em)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-emerald-600">{fmt(c.valor)}</span>
                        {c.status === "pago" ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <Badge variant="outline" className="text-[10px]">{c.status}</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </section>
          )}
        </>
      )}
    </div>
  );
}
